/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/label-has-for */

import { memo, useEffect, useRef, useState } from 'react';

import useForage from 'contexts/ForageContext/useForage';
import { Alert } from 'honeydew/Alert';
import { getEBTErrorMessage } from 'shared/ebt/EBTErrors';

import CheckBalanceButton from './CheckBalanceButton';
import { createPinElement, destroyPinElement } from './utils';

/**
 * ForagePinElement - 3rd party iframe with a secure PIN input field for EBT payment processing
 *
 * @param {Object} props - Component props
 * @param {Object} [props.balanceButtonProps] - Props to pass to the balance check button
 * @param {string} [props.elementID='pin-element-container'] - Unique ID for the PIN element container
 * @param {string} [props.error] - Error message to display below the PIN field
 * @param {Function} [props.onChange] - Callback fired when PIN input changes (event, isValid)
 * @param {Function} [props.onReady] - Callback fired when PIN element is ready (element)
 * @param {boolean} [props.showBalanceButton=false] - Whether to show the balance check button on top of the field
 *
 * @example
 * <ForagePinElement
 *   elementID="my-pin-element"
 *   onChange={(event, isValid) => console.log('PIN changed:', isValid)}
 *   onReady={(element) => console.log('PIN element ready')}
 *   showBalanceButton
 *   error={pinError}
 * />
 */
function ForagePinElement({
    balanceButtonProps = {},
    elementID = 'pin-element-container',
    error,
    onChange,
    onReady,
    showBalanceButton = false,
}) {
    const { error: forageError, forage, isLoading: isForageLoading, isReady: isForageReady } = useForage();

    const elementRef = useRef(null);
    const isInitializing = useRef(false);
    const onReadyRef = useRef(onReady);
    const onChangeRef = useRef(onChange);

    const [isReady, setIsReady] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [createError, setCreateError] = useState(null);

    const errorMessage = getEBTErrorMessage(error) || error?.message || 'A valid PIN is required. Please try again.';

    useEffect(() => {
        if (isForageLoading || !isForageReady || isInitializing.current || elementRef.current) return;

        const init = async () => {
            isInitializing.current = true;
            setCreateError(null);

            try {
                const pinElement = await createPinElement({
                    forage,
                    elementID,
                    onReady: (element) => {
                        onReadyRef.current?.(element);
                    },
                    onChange: (event) => {
                        const valid = event.complete && !event.error;
                        setIsValid(valid);
                        onChangeRef.current?.(event, valid);
                    },
                });

                // Element created & mounted successfully
                if (pinElement) {
                    elementRef.current = pinElement;
                    setIsReady(true);
                }
            } catch (initError) {
                setCreateError(initError);
                elementRef.current = null;
                setIsReady(false);
            } finally {
                isInitializing.current = false;
            }
        };

        init();
    }, [isForageLoading, isForageReady, elementID]);

    useEffect(
        () => () => {
            if (elementRef.current) {
                destroyPinElement(elementRef.current);
            }
        },
        []
    );

    if (forageError || createError) {
        return <Alert variant="error">Sorry, there was an error. Please refresh the page & try again.</Alert>;
    }

    return (
        <div className="relative" data-testid="forage-pin-element-container">
            {isReady && (
                <>
                    <label className="text-body-xs absolute -top-[9px] left-10 z-10 rounded-md bg-white px-5 font-grotesk-bold">
                        PIN *
                    </label>
                    {showBalanceButton && (
                        <div className="absolute right-20 top-15">
                            <CheckBalanceButton isPinValid={isValid} {...balanceButtonProps} />
                        </div>
                    )}
                </>
            )}
            {/* Using aria-label instead of htmlFor since divs cannot be labeled according to HTML spec */}
            <div
                id={elementID}
                style={{ height: '75px' }}
                className={`transition-opacity duration-200 ${isReady ? 'opacity-100' : 'opacity-0'}`}
                aria-label="PIN Entry Field"
            />
            {error && <p className="text-body-xs mt-10 text-error">{errorMessage}</p>}
        </div>
    );
}

export default memo(ForagePinElement);
